<template>
  <!-- The message content is sent by the API as HTML. -->
  <!-- eslint-disable vue/no-v-html -->
  <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
  <div
    class="whitespace-pre-line break-words [&_a]:underline [&_a]:[overflow-wrap:anywhere]"
    v-html="parsedContent"
  />
  <!-- eslint-enable -->
</template>

<script setup lang="ts">
/**
 * @example
 * ```vue
 * <!-- Basic usage with auto-link conversion -->
 * <MessageContent content="Hello! Visit https://backmarket.com for more info." />
 *
 * <!-- Preserve existing HTML links -->
 * <MessageContent
 *   content='Check <a href="https://help.backmarket.com">our help</a>'
 *   :should-format-links="true"
 * />
 *
 * <!-- Disable link formatting (escape all HTML) -->
 * <MessageContent
 *   content="Raw message with script tags"
 *   :should-format-links="false"
 * />
 * ```
 */
import { computed } from 'vue'

import { isEmpty } from '@backmarket/utils/object/isEmpty'

interface Props {
  /**
   * The raw message content to be processed and displayed.
   * Can contain HTML tags, line breaks, and URLs.
   *
   * @example "Hello <b>world</b>!\nVisit https://example.com"
   */
  content: string

  /**
   * Whether to preserve existing HTML <a> tags and allow link formatting.
   *
   * When `true` (default):
   * - Existing <a> tags are preserved with their attributes
   * - Plain URLs are automatically converted to clickable links
   * - Other HTML tags are still escaped for security
   *
   * When `false`:
   * - All HTML tags (including <a>) are escaped to &lt;&gt;
   * - Plain URLs are still converted to clickable links
   * - Maximum security for untrusted content
   *
   * @default true
   * @example
   * // With shouldFormatLinks: true
   * // '<a href="https://help.com">Help</a>' becomes clickable link
   *
   * // With shouldFormatLinks: false
   * // '<a href="https://help.com">Help</a>' becomes escaped text
   */
  shouldFormatLinks?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  shouldFormatLinks: true,
})

/**
 * Processes the content string to handle HTML formatting while preventing XSS.
 *
 * The processing steps are:
 * 1. Replace line breaks and normalize paragraphs
 * 2. Preserve valid <a> tags (if shouldFormatLinks is enabled)
 * 3. Escape remaining HTML to prevent script injection
 * 4. Convert plain URLs to clickable links
 *
 * @returns {string} The processed content ready for display
 */
const parsedContent = computed(() => {
  if (isEmpty(props.content)) return ''

  // First replace all line breaks and normalize paragraphs
  let wrappedContent = props.content
    // Handle all variations of <br> tags
    .replace(/<br\s*\/?>/gi, '\n')
    // Handle empty paragraphs with just a newline
    .replace(/<p[^>]*>\s*<\/p>/gi, '\n')
    // Handle regular paragraphs - preserve the line break effect but remove the tags
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/p>/gi, '\n')
    // Handle headers
    .replace(/<h1[^>]*>/gi, '')
    .replace(/<\/h1>/gi, '\n')
    .replace(/<h2[^>]*>/gi, '')
    .replace(/<\/h2>/gi, '\n')
    // Handle text formatting
    .replace(/<b[^>]*>/gi, '')
    .replace(/<\/b>/gi, '')
    .replace(/<i[^>]*>/gi, '')
    .replace(/<\/i>/gi, '')
    // Handle code blocks
    .replace(/<code[^>]*>/gi, '')
    .replace(/<\/code>/gi, '')
    .replace(/<pre[^>]*>/gi, '')
    .replace(/<\/pre>/gi, '\n')
    // Normalize multiple consecutive newlines to a maximum of two
    .replace(/\n{3,}/g, '\n\n')

  if (props.shouldFormatLinks) {
    // Save all valid <a> tags to restore them later
    const links: string[] = []
    wrappedContent = wrappedContent.replace(/<a[^>]*>.*?<\/a>/gi, (match) => {
      links.push(match)

      return `%%LINK${links.length - 1}%%`
    })

    // Escape all remaining HTML to prevent script injection
    let escapedContent = wrappedContent
      .replace(/</gi, '&lt;')
      .replace(/>/gi, '&gt;')

    // Restore saved links
    links.forEach((link, index) => {
      escapedContent = escapedContent.replace(`%%LINK${index}%%`, link)
    })

    // Convert plain URLs to clickable links, but only if they're not already part of an <a> tag
    return escapedContent.replace(
      /(^|[^"])(https?:\/\/[^\s]+?)([.!?;,]*)(\s|$)/gi,
      '$1<a href="$2" target="_blank" rel="noreferrer noopener">$2</a>$3$4',
    )
  }

  // If shouldFormatLinks is false, escape all HTML to prevent script injection
  return (
    wrappedContent
      .replace(/</gi, '&lt;')
      .replace(/>/gi, '&gt;')
      // Still convert plain URLs to clickable links
      .replace(
        /(^|[^"])(https?:\/\/[^\s]+?)([.!?;,]*)(\s|$)/gi,
        '$1<a href="$2" target="_blank" rel="noreferrer noopener">$2</a>$3$4',
      )
  )
})
</script>
